import { useCallback, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import { concat, differenceBy } from 'lodash';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/News/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES } from 'pages/News/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import LoadingIndicator from 'components/LoadingIndicator';
import ComponentPreview from 'components/PreviewWrapper';
import { SERIALIZER_OPTIONS } from 'app/constants';
import { convertKeysToSnakeCase, transformPosition, transformRemoved } from 'utils/helpers';
import parseDefaultValues from 'pages/News/Form/Edit/parseDefaultValues';
import DndWrapper from 'components/DndWrapper';
import ContentComponentsForm from 'components/Content/ContentComponentsForm';
import AddButton from 'components/AddButton';
import { useGetNewsItem } from 'api/news';
import FormWrapper from 'components/FormWrapper';

function NewsEdit() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  let debounce: ReturnType<typeof setTimeout>;

  const methods = useForm<News>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError, reset, control } = methods;

  const {
    fields: containers,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'contentContainersAttributes',
    keyName: 'fieldId',
    shouldUnregister: true,
  });

  const addContainerHandler = useCallback(() => {
    append({ kind: '' } as ContentContainer);
  }, [append]);

  const reorderContainers = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      clearTimeout(debounce);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      debounce = setTimeout(() => move(dragIndex, hoverIndex), 500);
    },
    [move],
  );

  const { data: { data: newsItem } = {}, isFetching } = useGetNewsItem(+id);

  const { mutate: editNewsItem, isLoading: isEditing } = useMutation({
    mutationKey: 'newsMutation',
    mutationFn: (data: News) => {
      const removedContainers = differenceBy<ContentContainer, ContentContainer>(
        newsItem?.contentContainers,
        data?.contentContainersAttributes,
        'id',
      );

      data.contentContainersAttributes = concat<ContentContainer>(
        ...transformPosition(data?.contentContainersAttributes),
        ...transformRemoved(removedContainers),
      );

      const snakePayload = convertKeysToSnakeCase(data);
      const formData = serialize(snakePayload, SERIALIZER_OPTIONS, new FormData(), 'news_item');
      return API.put(APIRoutes.news.byId(+id), formData);
    },
    onSuccess: () => {
      toast.success('Zmiany w artykule zostały zastosowane');
      navigate(ROUTES.news.base);
      void queryClient.invalidateQueries('news');
    },
    onError: ({ errors }) => {
      setFormErrors(setError, errors);
      toast.error('Wystąpił problem podczas edycji artykułu');
    },
  });

  const onSubmit = handleSubmit((data: News) => editNewsItem(data));

  useEffect(() => {
    if (newsItem) {
      reset({ ...DEFAULT_VALUES, ...parseDefaultValues(newsItem) });
    }
  }, [newsItem, reset]);

  const isLoading = isFetching || isEditing;

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: 'Aktualności',
          to: ROUTES.news.base,
        }}
        crumbs={[{ to: '', label: 'Edytuj artykuł' }]}
      />
      {isFetching && <LoadingIndicator />}
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="news-editor" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
            {containers.map((container, index) => (
              <DndWrapper
                key={`news-editor-${container.fieldId}`}
                id={container.fieldId}
                isLoading={isLoading}
                index={index}
                reorderContainers={reorderContainers}
                removeContainer={remove}
                ContainerWrapper={ContentComponentsForm}
                multipleContainers
              />
            ))}
            <AddButton addHandler={addContainerHandler} mt={2} />
          </Box>
          <ComponentPreview kind="news" isEditMode />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} formId="news-editor" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default NewsEdit;
